/* ----------------------------------------------------------------------------
 * Shared IP-geo helpers (used by the PDP disclaimer, the card badge, and
 * express-60.js — all read the same sessionStorage cache so only ONE network
 * lookup happens per visit).
 * ------------------------------------------------------------------------- */
const TB_GEO_CACHE_KEY = 'tb_geo_region';
/* One network lookup per page even when several components resolve at once
   (delivery-eta, card bars, countdowns and express-60 all call in on load). */
let tbRegionInflight = null;

async function tbLookupRegion() {
  const fetchJson = async (url, map) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error('geo http ' + res.status);
      return map(await res.json());
    } finally {
      clearTimeout(timer);
    }
  };

  try {
    return await fetchJson('https://ipwho.is/?fields=country_code,city,region,success', (d) => {
      if (d && d.success === false) throw new Error('geo lookup failed');
      return { country: d.country_code, city: d.city, region: d.region };
    });
  } catch (e) {
    return await fetchJson('https://ipapi.co/json/', (d) => ({
      country: d.country_code,
      city: d.city,
      region: d.region
    }));
  }
}

async function tbResolveRegion() {
  let region;
  try {
    region = JSON.parse(sessionStorage.getItem(TB_GEO_CACHE_KEY));
  } catch (e) {
    region = null;
  }
  if (!region) {
    if (!tbRegionInflight) {
      tbRegionInflight = tbLookupRegion().finally(() => {
        tbRegionInflight = null;
      });
    }
    region = await tbRegionInflight;
    try {
      sessionStorage.setItem(TB_GEO_CACHE_KEY, JSON.stringify(region));
    } catch (e) {
      /* sessionStorage unavailable (private mode) — ignore */
    }
  }
  return region;
}

function tbRegionAllowed(region, mode) {
  const country = (region.country || '').toUpperCase();
  const city = (region.city || '').toLowerCase();
  const area = (region.region || '').toLowerCase();
  if (country !== 'AE') return false;
  if (mode === 'uae') return true;
  return /dubai|دبي/.test(city) || /dubai|دبي/.test(area);
}

/* ----------------------------------------------------------------------------
 * Emirate resolution (UAE) — added 2026-07 for the by-emirate delivery ETA.
 *
 * Order of truth:
 *   1. an explicit pick the visitor made in the "Delivery Time Slot" selector
 *      (localStorage.selectedEmirate) — a manual choice always beats IP;
 *   2. otherwise the SHARED ip lookup (tb_geo_region) mapped city/region →
 *      emirate, so no extra network call is made.
 * Returns emirate: null when it can't be established → every caller stays
 * hidden (same fail-safe contract as the rest of this file).
 *
 * The needle table is ordered SPECIFIC → GENERIC on purpose: "marina mall"
 * (Abu Dhabi) must be tested before "marina" (Dubai Marina). Needles of 4
 * characters or fewer ("rak", "uaq", "jlt") are matched on word boundaries so
 * they can't fire inside an unrelated place name.
 * ------------------------------------------------------------------------- */
const TB_EMIRATE_CHANGE_EVENT = 'on:emirate:change';

const TB_EMIRATE_MAP = [
  // --- Al Ain (before Abu Dhabi: IP data reports city "Al Ain", region "Abu Dhabi") ---
  ['al ain', 'al-ain'],
  ['al-ain', 'al-ain'],
  ['العين', 'al-ain'],
  ['buraimi', 'al-ain'],
  // --- Abu Dhabi (before Dubai: "marina mall" must beat "marina") ---
  ['marina mall', 'abu-dhabi'],
  ['abu dhabi', 'abu-dhabi'],
  ['abudhabi', 'abu-dhabi'],
  ['abu zaby', 'abu-dhabi'],
  ['أبوظبي', 'abu-dhabi'],
  ['أبو ظبي', 'abu-dhabi'],
  ['khalifa city', 'abu-dhabi'],
  ['masdar', 'abu-dhabi'],
  ['yas island', 'abu-dhabi'],
  ['saadiyat', 'abu-dhabi'],
  ['reem island', 'abu-dhabi'],
  ['al raha', 'abu-dhabi'],
  ['madinat zayed', 'abu-dhabi'],
  ['ruwais', 'abu-dhabi'],
  ['corniche', 'abu-dhabi'],
  // --- Ras Al Khaimah ---
  ['ras al khaimah', 'ras-al-khaimah'],
  ['ras al-khaimah', 'ras-al-khaimah'],
  ['ras al khaymah', 'ras-al-khaimah'],
  ['رأس الخيمة', 'ras-al-khaimah'],
  ['al hamra', 'ras-al-khaimah'],
  ['rak', 'ras-al-khaimah'],
  // --- Umm Al Quwain ---
  ['umm al quwain', 'umm-al-quwain'],
  ['umm al-quwain', 'umm-al-quwain'],
  ['umm ul quain', 'umm-al-quwain'],
  ['أم القيوين', 'umm-al-quwain'],
  ['uaq', 'umm-al-quwain'],
  // --- Fujairah ---
  ['fujairah', 'fujairah'],
  ['الفجيرة', 'fujairah'],
  ['khor fakkan', 'fujairah'],
  ['dibba', 'fujairah'],
  // --- Ajman ---
  ['ajman', 'ajman'],
  ['عجمان', 'ajman'],
  ['al nuaimiya', 'ajman'],
  // --- Sharjah ---
  ['sharjah', 'sharjah'],
  ['الشارقة', 'sharjah'],
  ['al nahda', 'sharjah'],
  ['muwaileh', 'sharjah'],
  ['university city', 'sharjah'],
  ['al majaz', 'sharjah'],
  ['al qasba', 'sharjah'],
  // --- Dubai ---
  ['dubai', 'dubai'],
  ['دبي', 'dubai'],
  ['deira', 'dubai'],
  ['bur dubai', 'dubai'],
  ['jumeirah', 'dubai'],
  ['downtown', 'dubai'],
  ['business bay', 'dubai'],
  ['internet city', 'dubai'],
  ['media city', 'dubai'],
  ['knowledge village', 'dubai'],
  ['silicon oasis', 'dubai'],
  ['al barsha', 'dubai'],
  ['sheikh zayed road', 'dubai'],
  ['mirdif', 'dubai'],
  ['al quoz', 'dubai'],
  ['marina', 'dubai'],
  ['jlt', 'dubai'],
  ['jbr', 'dubai']
];

function tbMapToEmirate(city, region) {
  const hay = ((city || '') + ' ' + (region || '')).toLowerCase().trim();
  if (!hay) return null;
  for (let i = 0; i < TB_EMIRATE_MAP.length; i += 1) {
    const needle = TB_EMIRATE_MAP[i][0];
    if (needle.length <= 4) {
      if (new RegExp('(^|[^a-z0-9])' + needle + '($|[^a-z0-9])').test(hay)) return TB_EMIRATE_MAP[i][1];
    } else if (hay.indexOf(needle) !== -1) {
      return TB_EMIRATE_MAP[i][1];
    }
  }
  return null;
}

function tbStoredEmirate() {
  try {
    const value = (localStorage.getItem('selectedEmirate') || '').trim().toLowerCase();
    if (!value) return null;
    // Only a HUMAN choice may outrank IP. The legacy Delivery Time Slot widget also
    // wrote this key from its own auto-detection, whose lookup table silently fell
    // back to 'dubai' for any town it didn't recognise — so returning visitors can
    // carry a stale, wrong 'dubai' that would promise them same-day. Those entries
    // (locationAutoDetected === 'true') are ignored in favour of a fresh resolve.
    if (localStorage.getItem('locationAutoDetected') === 'true') return null;
    return value;
  } catch (e) {
    return null; // localStorage unavailable (private mode)
  }
}

/* Persist a manual pick and let every listening component re-render at once. */
function tbSetEmirate(slug, autoDetected) {
  const value = (slug || '').trim().toLowerCase();
  if (!value) return;
  try {
    localStorage.setItem('selectedEmirate', value);
    localStorage.setItem('locationAutoDetected', autoDetected ? 'true' : 'false');
  } catch (e) {
    /* ignore — the event below still updates this page */
  }
  document.dispatchEvent(new CustomEvent(TB_EMIRATE_CHANGE_EVENT, { detail: { emirate: value } }));
}

/* → { emirate: 'dubai' | … | 'international' | null, source, inUae, country? }
   `country` (ISO-2, upper case) is set whenever the IP lookup produced one, so
   the GCC / Aramex branch of <delivery-eta> knows WHICH foreign country. */
async function tbResolveEmirate() {
  const manual = tbStoredEmirate();
  if (manual) return { emirate: manual, source: 'manual', inUae: manual !== 'international' };

  let region;
  try {
    region = await tbResolveRegion();
  } catch (e) {
    return { emirate: null, source: 'unknown', inUae: false }; // fail-safe
  }

  const country = (region.country || '').toUpperCase();
  if (!country) return { emirate: null, source: 'unknown', inUae: false };
  if (country !== 'AE') return { emirate: 'international', source: 'ip', inUae: false, country };
  return { emirate: tbMapToEmirate(region.city, region.region), source: 'ip', inUae: true, country };
}

/* The shopper's most recent EXPLICIT choice must win (2026-10-06). Two choices
   can disagree: a UAE emirate picked in our delivery picker (localStorage) and
   the Shopify market from the country selector (localization cookie, which can
   also be stale — set once, or by a ?country= link — and outlives a VPN session).
   We remember the last market we saw; when it CHANGES to a non-UAE country the
   shopper has just switched country, so an older emirate pick is dropped. */
const TB_LAST_MARKET_KEY = 'tb_last_market';
/* Set when the market CHANGES to the UAE. AE is also the store's default market,
   so "AE" alone can't tell a deliberate choice from an untouched selector — only
   the change can. It means "they chose the UAE": beats a GCC IP (e.g. a VPN exit).
   It names no emirate, so delivery stays on the neutral, dateless line until
   they pick one (fail-safe). */
const TB_MARKET_UAE_CHOSEN_KEY = 'tb_market_uae_chosen';

/* The shopper explicitly chose a country (the country selector, or a detected
   market change). UAE → remember "they chose the UAE"; anything else → forget
   any older UAE choice so the new country takes effect. */
function tbRecordCountryChoice(country) {
  const code = String(country || '').toUpperCase();
  if (!code) return;
  try {
    if (code === 'AE') {
      localStorage.setItem(TB_MARKET_UAE_CHOSEN_KEY, '1');
    } else {
      localStorage.removeItem(TB_MARKET_UAE_CHOSEN_KEY);
      localStorage.removeItem('selectedEmirate');
      localStorage.removeItem('locationAutoDetected');
    }
    localStorage.setItem(TB_LAST_MARKET_KEY, code);
  } catch (e) {
    /* localStorage unavailable (private mode) */
  }
}

/* Catches market changes that didn't go through our selector hook (a ?country=
   link, another tab). The selector itself calls tbRecordCountryChoice directly
   (snippets/localization-form.liquid), which also covers re-choosing the UAE
   while the market already IS the UAE — e.g. a Saudi VPN exit, where Shopify
   never left the default market and so nothing would "change". */
function tbSyncMarketChoice(market) {
  if (!market) return;
  try {
    const previous = localStorage.getItem(TB_LAST_MARKET_KEY);
    if (previous && previous !== market) tbRecordCountryChoice(market);
    else if (previous !== market) localStorage.setItem(TB_LAST_MARKET_KEY, market);
  } catch (e) {
    /* localStorage unavailable (private mode) — nothing stored to reconcile */
  }
}
function tbMarketUaeChosen() {
  try {
    return localStorage.getItem(TB_MARKET_UAE_CHOSEN_KEY) === '1';
  } catch (e) {
    return false;
  }
}

// Reconcile ONCE, before any component (delivery-eta, card bars, express-60,
// gcc-currency) reads the emirate — so a single page never mixes old and new.
tbSyncMarketChoice(String(window.tbMarketCountry || '').toUpperCase());

/* ISO-2 code of the shopper's country when it is one of `wanted` (e.g. the GCC
   list), otherwise null. Shared by the GCC/Aramex delivery promise and the GCC
   currency display so the two can never disagree about where someone is.
   Order of truth:
     1. a manual UAE emirate pick, or switching the country selector TO the UAE
        (they told us they're in the UAE — beats a stale country cookie or a
        VPN exit; see tbSyncMarketChoice);
     2. the Shopify market from the country selector (an explicit destination);
     3. the shared IP lookup.
   Unconfirmed → null (fail-safe: plain UAE behaviour). */
async function tbResolveCountry(marketCountry, wanted) {
  const list = (wanted || []).map((c) => String(c).toUpperCase());
  if (!list.length) return null;
  const market = String(marketCountry || '').toUpperCase();

  const manual = tbStoredEmirate();
  if (manual && manual !== 'international') return null;
  if (market === 'AE' && tbMarketUaeChosen()) return null;

  if (list.indexOf(market) !== -1) return market;

  let result;
  try {
    result = await tbResolveEmirate();
  } catch (e) {
    return null;
  }
  if (result.inUae) return null;
  let country = result.country;
  if (!country && result.emirate === 'international') {
    // A manual 'international' pick carries no country — fall back to the
    // (cached, shared) IP lookup rather than guessing.
    try {
      country = (await tbResolveRegion()).country;
    } catch (e) {
      country = null;
    }
  }
  country = String(country || '').toUpperCase();
  return list.indexOf(country) !== -1 ? country : null;
}

/* True only when the shopper is CONFIRMED outside the UAE (2026-11-03, UAE-only features:
   trade-in page + PDP banner, UAE-only announcement slides). Built from the resolvers above so it
   can never disagree with the GCC prices / delivery promise:
     1. a manual delivery-area pick is an explicit answer: a UAE emirate → false, 'international' → true;
     2. otherwise tbResolveCountry's precedence (choosing the UAE in the country selector beats the
        IP; a non-UAE market beats the IP) decides between the IP country and the market.
   Unknown → false (fail-open: the feature shows; most visitors are in the UAE). */
async function tbResolveOutsideUae() {
  const market = String(window.tbMarketCountry || '').toUpperCase();
  let ipCountry = '';
  try {
    const result = await tbResolveEmirate();
    if (result && result.source === 'manual') return result.emirate === 'international';
    ipCountry = String((result && result.country) || '').toUpperCase();
  } catch (e) {
    ipCountry = '';
  }
  const candidates = [ipCountry, market].filter((c) => c && c !== 'AE');
  if (!candidates.length) return false;
  try {
    return !!(await tbResolveCountry(market, candidates));
  } catch (e) {
    return false;
  }
}

/* Expose for other components (e.g. express-60.js) if they want to reuse it. */
window.tbGeo = {
  resolveRegion: tbResolveRegion,
  regionAllowed: tbRegionAllowed,
  resolveEmirate: tbResolveEmirate,
  resolveCountry: tbResolveCountry,
  resolveOutsideUae: tbResolveOutsideUae,
  recordCountryChoice: tbRecordCountryChoice,
  mapToEmirate: tbMapToEmirate,
  setEmirate: tbSetEmirate,
  storedEmirate: tbStoredEmirate,
  EMIRATE_CHANGE_EVENT: TB_EMIRATE_CHANGE_EVENT
};

/* ----------------------------------------------------------------------------
 * Time helpers for the daily "hide window" (HH:MM, store time / GST).
 * ------------------------------------------------------------------------- */
function tbParseHM(value) {
  if (!value) return null;
  const m = String(value).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/* True when nowMin is inside [from, to) — supports ranges that wrap past midnight. */
function tbWithinWindow(nowMin, from, to) {
  if (from == null || to == null || from === to) return false;
  if (from < to) return nowMin >= from && nowMin < to;
  return nowMin >= from || nowMin < to;
}

/* Current time in store time (GST / UTC+4), regardless of the visitor's TZ. */
function tbGetGSTDate() {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utcMs + 4 * 3600000);
}

/* minutes-since-midnight → "HH:MM" (for printing a cutoff back to the shopper) */
function tbFormatHM(minutes) {
  if (minutes == null) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return (h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m;
}

/* remaining minutes → "3h 12m" / "42m" */
function tbFormatRemaining(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? h + 'h ' + m + 'm' : m + 'm';
}

/* "0,6" → [0, 6]; used for non-delivery weekdays (0 = Sunday). */
function tbParseDayList(value) {
  return String(value || '')
    .split(',')
    .map((d) => parseInt(d.trim(), 10))
    .filter((d) => !isNaN(d) && d >= 0 && d <= 6);
}

/* First date an order placed now can actually arrive: today when we're still
   before the cutoff, otherwise tomorrow — then rolled forward past any
   configured non-delivery weekday. */
function tbDeliveryDate(gst, beforeCutoff, skipDays) {
  const date = new Date(gst.getTime());
  if (!beforeCutoff) date.setDate(date.getDate() + 1);
  let guard = 0;
  while (skipDays.indexOf(date.getDay()) !== -1 && guard < 8) {
    date.setDate(date.getDate() + 1);
    guard += 1;
  }
  return date;
}

function tbFormatDate(date) {
  try {
    const lang = document.documentElement.lang || 'en';
    return new Intl.DateTimeFormat(lang, { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
  } catch (e) {
    return date.getDate() + '/' + (date.getMonth() + 1);
  }
}

/* Fill [emirate] / [time] / [date] / [cutoff] / [country] placeholders in an editor string. */
function tbFillTemplate(template, vars) {
  return String(template || '').replace(/\[(emirate|time|date|cutoff|country)\]/g, (match, key) => {
    return vars[key] == null ? '' : vars[key];
  });
}

/* ----------------------------------------------------------------------------
 * Card badge visibility.
 * The EXPRESS badge mirrors the 60-minute toggle's OWN rules so the two stay in
 * sync: region gating (express60_geo_mode) AND daily availability windows
 * (express60_open1/2). Both are GLOBAL — the visitor's region and the current
 * time are the same for every card on the page — so we resolve them ONCE and
 * toggle a single root class (.tb-express-badge-ok) that reveals every managed
 * badge at once. No per-card lookup, no Liquid loops on collection pages.
 * Fail-safe: managed badges stay hidden until both checks pass.
 *
 * Badges with neither gating active carry no .express-badge--managed class and
 * are server-rendered visible, so this init is a no-op for them.
 * ------------------------------------------------------------------------- */
async function tbResolveGeoOk(geoMode) {
  if (geoMode === 'off') return true;
  try {
    // Single source of truth with <delivery-eta> / the next-day bar: the visitor's
    // manual emirate pick outranks IP, and IP is mapped through the emirate table
    // (so "Deira" or a carrier IP labelled by locality still counts as Dubai).
    // The raw /dubai/ regex (tbRegionAllowed) is no longer consulted here — it
    // contradicted the emirate system and made the promises drift apart.
    const resolved = await tbResolveEmirate();
    if (geoMode === 'uae') return resolved.inUae;
    return resolved.emirate === 'dubai';
  } catch (e) {
    return false; // fail-safe: keep hidden when location can't be confirmed
  }
}

async function initDeliveryBars() {
  const root = document.documentElement;

  // Every bar registers a refresher so a manual emirate change (same page) or a
  // BFCache restore (emirate changed on ANOTHER page, e.g. the PDP picker, then
  // the visitor navigated back here) re-resolves geo and re-applies the root
  // classes instead of serving the state frozen at first load.
  const refreshers = [];

  // --- Express 60 top bar (per-product eligibility; GLOBAL geo + availability windows). ---
  // One managed bar carries the shared gating config; toggling .tb-express-badge-ok reveals
  // every managed express bar at once. (Class name kept for continuity with existing CSS.)
  const express = document.querySelector('.card-delivery-bar--express.card-delivery-bar--managed');
  if (express) {
    const geoMode = (express.dataset.geoMode || 'off').toLowerCase();
    const windows = [
      { from: tbParseHM(express.dataset.open1From), to: tbParseHM(express.dataset.open1To) },
      { from: tbParseHM(express.dataset.open2From), to: tbParseHM(express.dataset.open2To) }
    ].filter((w) => w.from != null && w.to != null && w.from !== w.to);
    // Friday override — when any Friday window is configured, Fridays use ONLY these.
    const fridayWindows = [
      { from: tbParseHM(express.dataset.friOpen1From), to: tbParseHM(express.dataset.friOpen1To) },
      { from: tbParseHM(express.dataset.friOpen2From), to: tbParseHM(express.dataset.friOpen2To) }
    ].filter((w) => w.from != null && w.to != null && w.from !== w.to);
    const closedDays = tbParseDayList(express.dataset.closedDays);

    let geoOk = await tbResolveGeoOk(geoMode);
    const withinWindow = () => {
      const gst = tbGetGSTDate();
      if (closedDays.indexOf(gst.getDay()) !== -1) return false; // office closed — no 60-min runs
      const active = gst.getDay() === 5 && fridayWindows.length ? fridayWindows : windows;
      if (!active.length) return true;
      const nowMin = gst.getHours() * 60 + gst.getMinutes();
      return active.some((w) => tbWithinWindow(nowMin, w.from, w.to));
    };
    const apply = () => root.classList.toggle('tb-express-badge-ok', geoOk && withinWindow());
    apply();
    if (windows.length || fridayWindows.length || closedDays.length) setInterval(apply, 30000);
    refreshers.push(async () => {
      geoOk = await tbResolveGeoOk(geoMode);
      apply();
    });
  }

  // --- Same-day delivery bar (ALL eligible products; GLOBAL geo + a daily cutoff). ---
  // Fail-safe hidden: only revealed when the visitor is in-region AND before the cutoff, so we
  // never render a same-day promise we can't stand behind (no JS / out of region → stays hidden).
  const sameday = document.querySelector('.card-delivery-bar--sameday');
  if (sameday) {
    const geoMode = (sameday.dataset.geoMode || 'off').toLowerCase();
    const cutoff = tbParseHM(sameday.dataset.cutoff);
    const closedDays = tbParseDayList(sameday.dataset.closedDays);
    let geoOk = await tbResolveGeoOk(geoMode);
    const beforeCutoff = () => {
      if (cutoff == null) return true;
      const gst = tbGetGSTDate();
      return gst.getHours() * 60 + gst.getMinutes() < cutoff;
    };
    // Office-closed day → the bar stays up all day but flips to its
    // "Delivery tomorrow" copy (.tb-sameday-closed swaps the spans + tint).
    const apply = () => {
      const closedToday = closedDays.indexOf(tbGetGSTDate().getDay()) !== -1;
      root.classList.toggle('tb-sameday-closed', closedToday);
      root.classList.toggle('tb-sameday-ok', geoOk && (closedToday || beforeCutoff()));
    };
    apply();
    if (cutoff != null || closedDays.length) setInterval(apply, 30000);
    refreshers.push(async () => {
      geoOk = await tbResolveGeoOk(geoMode);
      apply();
    });
  }

  // --- Next-day delivery bar (visitors OUTSIDE the same-day zone, e.g. Sharjah). ---
  // Same "resolve once, toggle a root class" contract as the two bars above: the
  // emirate and the clock are page-wide, so one lookup drives every card.
  //   .tb-nextday-ok   → visitor is in the UAE but NOT in the same-day zone
  //   .tb-after-cutoff → past the outside-Dubai cutoff, so the bar reads "next day"
  // Fail-safe: an unresolved emirate leaves both classes off and nothing is promised.
  const nextday = document.querySelector('.card-delivery-bar--nextday');
  if (nextday) {
    const zone = (nextday.dataset.sameDayZone || 'dubai')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const cutoff = tbParseHM(nextday.dataset.cutoff);
    const skipDays = tbParseDayList(nextday.dataset.skipDays);
    const showBefore = nextday.dataset.showBeforeCutoff !== 'false';

    let outsideZone = false;
    try {
      const resolved = await tbResolveEmirate();
      outsideZone = !!resolved.emirate && resolved.inUae && zone.indexOf(resolved.emirate) === -1;
    } catch (e) {
      outsideZone = false; // fail-safe: no bar when the area can't be confirmed
    }

    const apply = () => {
      const gst = tbGetGSTDate();
      const nowMin = gst.getHours() * 60 + gst.getMinutes();
      const before = cutoff == null ? false : nowMin < cutoff;
      const isToday = before && skipDays.indexOf(gst.getDay()) === -1;
      root.classList.toggle('tb-after-cutoff', !isToday);
      root.classList.toggle('tb-nextday-ok', outsideZone && (isToday ? showBefore : true));
    };
    apply();
    setInterval(apply, 30000);
    refreshers.push(async () => {
      try {
        const r = await tbResolveEmirate();
        outsideZone = !!r.emirate && r.inUae && zone.indexOf(r.emirate) === -1;
      } catch (e) {
        outsideZone = false;
      }
      apply();
    });
  }

  // --- Bulky cards (2026-08-26): the green same-day bar stands down once the
  // bulky cutoff has passed, or all day on the bulky next-day weekdays. Purely
  // time-based and page-wide, so one root class (.tb-bulky-nextday) covers every
  // card flagged .card-delivery-bar--bulky; the CSS hides their same-day bar.
  const bulky = document.querySelector('.card-delivery-bar--bulky[data-bulky-cutoff]');
  if (bulky) {
    const cutoff = tbParseHM(bulky.dataset.bulkyCutoff);
    const alldayDays = tbParseDayList(bulky.dataset.bulkyAlldayDays);
    const apply = () => {
      const gst = tbGetGSTDate();
      const nowMin = gst.getHours() * 60 + gst.getMinutes();
      const nextOnly = alldayDays.indexOf(gst.getDay()) !== -1 || (cutoff != null && nowMin >= cutoff);
      root.classList.toggle('tb-bulky-nextday', nextOnly);
    };
    apply();
    setInterval(apply, 30000);
  }

  if (refreshers.length) {
    const refreshAll = () => refreshers.forEach((fn) => fn());
    document.addEventListener(TB_EMIRATE_CHANGE_EVENT, refreshAll);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refreshAll();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDeliveryBars, { once: true });
} else {
  initDeliveryBars();
}

/* ----------------------------------------------------------------------------
 * <express-delivery-countdown> — PDP same-day delivery disclaimer.
 * ------------------------------------------------------------------------- */
class ExpressDeliveryCountdown extends HTMLElement {
  constructor() {
    super();
    this.intervalId = null;
  }

  connectedCallback() {
    this.dayEl = this.querySelector('[data-delivery-day]');
    this.countdownWrapper = this.querySelector('[data-countdown-wrapper]');
    this.countdownTimer = this.querySelector('[data-countdown-timer]');

    this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
    this.cutoffMinute = parseInt(this.dataset.cutoffMinute, 10);
    this.showCountdown = this.dataset.showCountdown === 'true';
    this.afterCutoffText = this.dataset.afterCutoffText;
    // Weekdays (0=Sun…6=Sat) where the promise is next-day ALL day: the
    // weekday text is shown regardless of the time, no cutoff/countdown.
    // Bulky items use this for Fridays — every Friday order goes out on the
    // Saturday run, so a cutoff race would be a false deadline.
    this.alldayDays = (this.dataset.alldayDays || '')
      .split(',')
      .map((d) => parseInt(d, 10))
      .filter((d) => d >= 0 && d <= 6);
    this.geoMode = (this.dataset.geoMode || 'off').toLowerCase();
    this.hideFrom = tbParseHM(this.dataset.hideFrom);
    this.hideTo = tbParseHM(this.dataset.hideTo);

    this.dayTexts = [
      this.dataset.sundayText,
      this.dataset.mondayText,
      this.dataset.tuesdayText,
      this.dataset.wednesdayText,
      this.dataset.thursdayText,
      this.dataset.fridayText,
      this.dataset.saturdayText
    ];

    // Geo gates through the shared emirate resolver (manual pick beats IP) and
    // re-resolves on an emirate change or a BFCache restore — same pattern as
    // the same-day tile / card bars. Hidden, never removed, so a later pick of
    // an allowed emirate can still reveal the block.
    this.geoOk = false;
    this.onGeoRefresh = () => this.resolveVisibility();
    this.onPageShow = (event) => {
      if (event.persisted) this.resolveVisibility();
    };
    document.addEventListener(TB_EMIRATE_CHANGE_EVENT, this.onGeoRefresh);
    window.addEventListener('pageshow', this.onPageShow);

    this.resolveVisibility();
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    document.removeEventListener(TB_EMIRATE_CHANGE_EVENT, this.onGeoRefresh);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  async resolveVisibility() {
    this.geoOk = await tbResolveGeoOk(this.geoMode);

    if (this.geoOk) {
      this.reveal();
    } else {
      this.conceal();
    }
  }

  reveal() {
    this.removeAttribute('hidden');
    this.classList.remove('express-delivery-block--geo-pending');
    this.start();
  }

  conceal() {
    this.hidden = true;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  start() {
    this.update();
    if (!this.intervalId) {
      this.intervalId = setInterval(() => this.update(), 60000);
    }
  }

  getGSTDate() {
    return tbGetGSTDate();
  }

  update() {
    // Geo gate lost (emirate changed to a disallowed one) — stay hidden.
    if (!this.geoOk && this.geoMode !== 'off') {
      this.hidden = true;
      return;
    }

    const gst = this.getGSTDate();
    const day = gst.getDay();
    const currentMinutes = gst.getHours() * 60 + gst.getMinutes();

    // Daily hide window — fully hide the disclaimer during the configured range.
    if (tbWithinWindow(currentMinutes, this.hideFrom, this.hideTo)) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    // All-day next-day weekday: the promise never flips, so no cutoff race.
    if (this.alldayDays.includes(day)) {
      this.classList.add('express-delivery-block--before-cutoff');
      this.classList.remove('express-delivery-block--after-cutoff');
      this.dayEl.textContent = this.dayTexts[day];
      if (this.countdownWrapper) this.countdownWrapper.style.display = 'none';
      return;
    }

    const cutoffMinutes = this.cutoffHour * 60 + this.cutoffMinute;
    const beforeCutoff = currentMinutes < cutoffMinutes;

    this.classList.toggle('express-delivery-block--before-cutoff', beforeCutoff);
    this.classList.toggle('express-delivery-block--after-cutoff', !beforeCutoff);

    if (beforeCutoff) {
      this.dayEl.textContent = this.dayTexts[day];

      if (this.showCountdown && this.countdownWrapper) {
        const remaining = cutoffMinutes - currentMinutes;
        const hours = Math.floor(remaining / 60);
        const minutes = remaining % 60;
        this.countdownTimer.textContent = hours + 'h ' + minutes + 'm';
        this.countdownWrapper.style.display = '';
      }
    } else {
      this.dayEl.textContent = this.afterCutoffText;

      if (this.countdownWrapper) {
        this.countdownWrapper.style.display = 'none';
      }
    }
  }
}

customElements.define('express-delivery-countdown', ExpressDeliveryCountdown);

/* ----------------------------------------------------------------------------
 * <sameday-countdown> — PDP buy-box "Same-Day Delivery" tile (UX refresh 2026-Q3).
 * Self-contained: reads its own data-geo-mode + data-cutoff (from the global
 * "Same-day delivery bar" settings), resolves the region once (shared
 * tb_geo_region cache) and reveals itself only in-region AND before the cutoff
 * (fail-safe hidden, same contract as the card bar). While visible it ticks a
 * "order within Xh Ym" countdown and drains a progress bar towards the cutoff.
 * ------------------------------------------------------------------------- */
class SamedayCountdown extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.cutoff = tbParseHM(this.dataset.cutoff);
    this.geoMode = (this.dataset.geoMode || 'off').toLowerCase();
    this.closedDays = tbParseDayList(this.dataset.closedDays);
    this.textTemplate = this.dataset.textOrderWithin || 'Order within [time]';
    this.countdownEl = this.querySelector('[data-sameday-countdown]');
    this.titleEl = this.querySelector('[data-sameday-title]');
    this.titleText = this.titleEl ? this.titleEl.textContent : '';
    this.titleClosedText = this.dataset.textTitleClosed || 'Delivery Tomorrow';
    this.dateEl = this.querySelector('[data-sameday-date]');
    this.barEl = this.querySelector('.sameday-tile__bar');
    this.fillEl = this.querySelector('[data-sameday-fill]');
    this.geoOk = false;

    // Re-resolve when the shopper changes their emirate (picker on this page) or
    // when the page comes back from BFCache after a change made elsewhere.
    this.onGeoRefresh = () => this.resolveGeo();
    this.onPageShow = (event) => {
      if (event.persisted) this.resolveGeo();
    };
    document.addEventListener(TB_EMIRATE_CHANGE_EVENT, this.onGeoRefresh);
    window.addEventListener('pageshow', this.onPageShow);

    this.resolveGeo();
  }

  disconnectedCallback() {
    if (this.timer) clearInterval(this.timer);
    document.removeEventListener(TB_EMIRATE_CHANGE_EVENT, this.onGeoRefresh);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  resolveGeo() {
    tbResolveGeoOk(this.geoMode).then((ok) => {
      this.geoOk = ok;
      this.update();
      if (ok && this.cutoff != null && !this.timer) this.timer = setInterval(() => this.update(), 30000);
    });
  }

  formatShortDate(date) {
    try {
      const lang = document.documentElement.lang || 'en';
      return new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }).format(date);
    } catch (e) {
      return date.getDate() + '/' + (date.getMonth() + 1);
    }
  }

  update() {
    const gst = tbGetGSTDate();
    const nowMinutes = gst.getHours() * 60 + gst.getMinutes();
    const closedToday = this.closedDays.indexOf(gst.getDay()) !== -1;
    const visible = this.geoOk && this.cutoff != null && (closedToday || nowMinutes < this.cutoff);
    this.hidden = !visible;
    if (!visible) return;

    if (this.countdownEl) this.countdownEl.hidden = closedToday;
    if (this.barEl) this.barEl.hidden = closedToday;

    // Office closed today — the tile flips to "Delivery Tomorrow · <next open day>"
    // (no countdown, no draining bar: there is no cutoff to race against).
    if (closedToday) {
      if (this.titleEl) this.titleEl.textContent = this.titleClosedText;
      if (this.dateEl) this.dateEl.textContent = this.formatShortDate(tbDeliveryDate(gst, false, this.closedDays));
      return;
    }
    if (this.titleEl) this.titleEl.textContent = this.titleText;

    const remaining = this.cutoff - nowMinutes;
    const hours = Math.floor(remaining / 60);
    const minutes = remaining % 60;
    const time = hours > 0 ? hours + 'h ' + minutes + 'm' : minutes + 'm';
    if (this.countdownEl) this.countdownEl.textContent = this.textTemplate.replace('[time]', time);

    if (this.dateEl) this.dateEl.textContent = this.formatShortDate(gst);

    // Bar = time left until the cutoff, as a share of the whole ordering day (00:00 → cutoff).
    if (this.fillEl) {
      const pct = Math.max(4, Math.min(100, (remaining / this.cutoff) * 100));
      this.fillEl.style.width = pct + '%';
    }
  }
}

customElements.define('sameday-countdown', SamedayCountdown);

/* ----------------------------------------------------------------------------
 * <delivery-eta> — by-emirate delivery disclaimer (PDP buy card + cart).
 * Added 2026-07.
 *
 * Answers one question for the shopper: "when does this reach MY emirate?"
 *   • inside the same-day zone (Dubai by default) → the same-day promise,
 *     with a live countdown to the zone cutoff;
 *   • anywhere else in the UAE → same-day until the outside-Dubai cutoff
 *     (13:00 GST by default), then it flips to next-day and names the date.
 *
 * The emirate comes from tbResolveEmirate(): the visitor's manual pick wins,
 * otherwise the shared IP lookup. It re-renders on `on:emirate:change`, so
 * changing the area anywhere on the page updates every instance at once.
 *
 * Fail-safe hidden, like the rest of this file: an unresolved emirate (lookup
 * blocked, VPN, outside the UAE) shows nothing rather than a promise we can't
 * keep. All copy is passed in from Liquid as data-tpl-* strings with
 * [emirate] / [time] / [date] / [cutoff] placeholders, so it stays editable in
 * the theme editor and translatable through the locale files.
 *
 * Bulky / heavy items (data-bulky-cutoff present, 2026-08-26): the promise
 * follows the BULKY schedule — one cutoff for every emirate (the dedicated
 * vehicle leaves at 13:00, so Dubai's later courier cutoff does not apply) and
 * next-day ALL day on data-bulky-allday-days (Fridays). Past that point even
 * the unknown-area line says next-day, so the tile can never contradict the
 * "Heavy item delivery" block. Express-60 is a separate paid product with its
 * own windows and is deliberately not consulted here.
 * ------------------------------------------------------------------------- */
class DeliveryEta extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.geoMode = (this.dataset.geoMode || 'uae').toLowerCase();
    this.sameDayZone = (this.dataset.sameDayZone || 'dubai')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    this.zoneCutoff = tbParseHM(this.dataset.zoneCutoff);
    this.otherCutoff = tbParseHM(this.dataset.otherCutoff);
    this.skipDays = tbParseDayList(this.dataset.skipDays);
    this.showBeforeCutoff = this.dataset.showBeforeCutoff !== 'false';

    // Bulky schedule (absent attribute = not a bulky product / order).
    this.bulkyCutoff = tbParseHM(this.dataset.bulkyCutoff);
    this.isBulky = this.bulkyCutoff != null;
    this.bulkyAlldayDays = this.isBulky ? tbParseDayList(this.dataset.bulkyAlldayDays) : [];

    this.tpl = {
      zoneToday: this.dataset.tplZoneToday || '',
      zoneNext: this.dataset.tplZoneNext || '',
      otherToday: this.dataset.tplOtherToday || '',
      otherNext: this.dataset.tplOtherNext || '',
      orderWithin: this.dataset.tplOrderWithin || '',
      afterCutoff: this.dataset.tplAfterCutoff || '',
      closedToday: this.dataset.tplClosedToday || '',
      bulkyAllday: this.dataset.tplBulkyAllday || '',
      unknownTitle: this.dataset.tplUnknownTitle || '',
      unknownTitleNext: this.dataset.tplUnknownTitleNext || '',
      unknownSub: this.dataset.tplUnknownSub || '',
      intlTitle: this.dataset.tplIntlTitle || '',
      intlCarrier: this.dataset.tplIntlCarrier || '',
      tomorrow: this.dataset.tplTomorrow || '',
      timeHM: this.dataset.tplTimeHm || '',
      timeM: this.dataset.tplTimeM || ''
    };

    // GCC / Aramex promise (absent attribute = switched off in theme settings).
    this.intlCountries = (this.dataset.intlCountries || '')
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    this.marketCountry = (this.dataset.marketCountry || '').toUpperCase();

    // When the visitor's area can't be established (IP lookup blocked by a content
    // blocker, VPN, private mode…) the old behaviour was to render nothing at all,
    // which cost every ad-blocker user any sight of the delivery promise. Instead we
    // now state the POLICY without a date and let them pick their area. Still no
    // promise we can't keep — just not silence.
    this.unknownFallback = this.dataset.unknownFallback !== 'false';

    try {
      this.labels = JSON.parse(this.dataset.emirateLabels || '{}');
    } catch (e) {
      this.labels = {};
    }

    this.titleEl = this.querySelector('[data-eta-title]');
    this.subEl = this.querySelector('[data-eta-sub]');
    this.selectEl = this.querySelector('[data-eta-select]');
    this.changeEl = this.querySelector('.delivery-eta__change');
    this.changeLabelEl = this.querySelector('[data-eta-change-label]');
    this.carrierEl = this.querySelector('[data-eta-carrier]');
    this.carrierTextEl = this.querySelector('[data-eta-carrier-text]');
    this.shipEl = this.querySelector('[data-eta-ship]');
    this.shipLineEl = this.querySelector('[data-eta-ship-line]');
    this.initShipEstimate();

    // Border sweep (2026-10-26) — PDP only; the cart drawer stays calm.
    this.sweepEnabled = !this.classList.contains('delivery-eta--compact');
    this.sweepKey = null;
    this.addEventListener('animationend', (event) => {
      if (event.animationName.indexOf('delivery-eta-sweep') === 0) this.classList.remove('delivery-eta--sweep');
    });

    if (this.selectEl) {
      this.selectEl.addEventListener('change', () => tbSetEmirate(this.selectEl.value, false));
    }

    this.onEmirateChange = () => this.resolve();
    document.addEventListener(TB_EMIRATE_CHANGE_EVENT, this.onEmirateChange);
    // BFCache restore: the emirate may have been changed on another page.
    this.onPageShow = (event) => {
      if (event.persisted) this.resolve();
    };
    window.addEventListener('pageshow', this.onPageShow);

    this.resolve();
  }

  disconnectedCallback() {
    if (this.timer) clearInterval(this.timer);
    document.removeEventListener(TB_EMIRATE_CHANGE_EVENT, this.onEmirateChange);
    window.removeEventListener('pageshow', this.onPageShow);
    if (this.onVariantChange) document.removeEventListener('on:variant:change', this.onVariantChange);
    if (this.sweepObserver) this.sweepObserver.disconnect();
  }

  /* One light sweep around the border when a confirmed promise first shows or
     changes (area or date) — never on the 30s countdown tick, which re-runs
     update(). Waits until the card is on screen, so a mobile shopper who
     scrolls down to it still sees it. CSS gates the motion on reduced-motion. */
  queueSweep(key) {
    if (!this.sweepEnabled || key === this.sweepKey) return;
    this.sweepKey = key;
    if (!('IntersectionObserver' in window)) return;
    if (this.sweepObserver) this.sweepObserver.disconnect();
    this.sweepObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.sweepObserver.disconnect();
      this.sweepObserver = null;
      this.classList.remove('delivery-eta--sweep');
      void this.offsetWidth; // restart the animation if it is mid-run
      this.classList.add('delivery-eta--sweep');
    }, { threshold: 0.6 });
    this.sweepObserver.observe(this);
  }

  /* ---- GCC shipping estimate (2026-10-06) ------------------------------------
     Mirrors how the Aramex app prices checkout: Shopify sends it the VARIANT
     weight only, Aramex rounds up to the next 0.5 kg and charges
     first 0.5 kg + each additional 0.5 kg, then fuel surcharge; Shopify adds the
     merchant's markup. Rates (AED) live in theme settings — a country without
     rates, or an item without weight, shows no estimate (never a guess). */
  initShipEstimate() {
    this.ship = null;
    if (!this.shipEl || !this.dataset.shipRates) return;
    let rates;
    let grams = null;
    try {
      rates = JSON.parse(this.dataset.shipRates);
      if (this.dataset.shipGrams) grams = JSON.parse(this.dataset.shipGrams);
    } catch (e) {
      return;
    }
    const pct = (v) => {
      const n = parseFloat(v);
      return isFinite(n) && n > 0 ? n / 100 : 0;
    };
    this.ship = {
      rates,
      grams, // variant id → grams (PDP)
      cartGrams: this.dataset.shipCartGrams ? parseInt(this.dataset.shipCartGrams, 10) : null,
      variantId: this.dataset.shipVariant || null,
      tierHalfKilos: (() => {
        const kg = parseFloat(this.dataset.shipTierKg);
        return isFinite(kg) && kg > 0 ? Math.round(kg * 2) : null;
      })(),
      fuel: pct(this.dataset.shipFuel),
      markup: pct(this.dataset.shipMarkup),
      spread: pct(this.dataset.shipSpread)
    };
    if (grams) {
      this.onVariantChange = (event) => {
        const variant = event.detail && event.detail.variant;
        // Only the product this block belongs to (quick-buy / sticky bars carry others).
        if (!variant || !(String(variant.id) in this.ship.grams)) return;
        this.ship.variantId = String(variant.id);
        this.renderShipEstimate();
      };
      document.addEventListener('on:variant:change', this.onVariantChange);
    }
  }

  /* AED fils for the current item(s) to the given country, or null. */
  shipEstimateFils(country) {
    const ship = this.ship;
    if (!ship || !country) return null;
    const rate = ship.rates[country] || [];
    const first = parseFloat(rate[0]);
    const extra = parseFloat(rate[1]);
    if (!(first > 0) || !(extra >= 0)) return null;
    const grams = ship.grams ? parseInt(ship.grams[ship.variantId], 10) : ship.cartGrams;
    if (!(grams > 0)) return null;
    const halfKilos = Math.max(1, Math.ceil(grams / 500));
    // Optional weight tier: above the tier weight, a (usually higher) price per
    // additional 0.5 kg — blank means the single additional price throughout.
    const heavy = parseFloat(rate[2]);
    let base;
    if (heavy > 0 && ship.tierHalfKilos && halfKilos > ship.tierHalfKilos) {
      base = first + extra * (ship.tierHalfKilos - 1) + heavy * (halfKilos - ship.tierHalfKilos);
    } else {
      base = first + extra * (halfKilos - 1);
    }
    const aed = base * (1 + ship.fuel) * (1 + ship.markup);
    return Math.round(aed * 100);
  }

  renderShipEstimate() {
    if (!this.shipEl) return;
    const fils = this.intlCountry ? this.shipEstimateFils(this.intlCountry) : null;
    if (fils == null || !this.shipLineEl) {
      this.shipEl.hidden = true;
      return;
    }
    // Round outward to whole AED; the upper end absorbs fuel changes + rounding.
    const low = Math.floor(fils / 100) * 100;
    const high = Math.ceil((fils * (1 + this.ship.spread)) / 100) * 100;
    const single = high <= low;
    const tpl = single ? this.shipLineEl.dataset.tplSingle : this.shipLineEl.dataset.tplRange;
    // Prices go through the shared money contract (snippets/fx-money.liquid), so
    // gcc-currency.js shows them in SAR/QAR/… like every other price.
    // The upper end of a range is "bare" (number only): "SAR 113–126".
    const money = (value, bare) => {
      const span = document.createElement('span');
      span.className = 'tb-fx';
      span.setAttribute('data-tb-fx', String(value));
      span.setAttribute('data-tb-fx-compact', '');
      if (bare) span.setAttribute('data-tb-fx-bare', '');
      const amount = Math.round(value / 100).toLocaleString('en-US');
      span.textContent = bare ? amount : 'AED ' + amount;
      return span;
    };
    this.shipLineEl.textContent = '';
    // Everything from [low] to [high] ("SAR 113–126" / "من ر.س 113 إلى 126") sits in
    // one nowrap span so a narrow column never breaks the range itself.
    const parts = String(tpl || '').split(/(\[low\]|\[high\])/);
    const first = parts.findIndex((p) => p === '[low]' || p === '[high]');
    let last = -1;
    parts.forEach((p, i) => { if (p === '[low]' || p === '[high]') last = i; });
    const range = document.createElement('span');
    range.className = 'delivery-eta__ship-range';
    parts.forEach((part, i) => {
      const target = i >= first && i <= last && first !== -1 ? range : this.shipLineEl;
      if (i === first && first !== -1) this.shipLineEl.appendChild(range);
      if (part === '[low]') target.appendChild(money(low));
      else if (part === '[high]') target.appendChild(money(high, true));
      else if (part) target.appendChild(document.createTextNode(part));
    });
    this.shipEl.hidden = false;
  }

  async resolve() {
    let result = { emirate: null, inUae: false };
    try {
      result = await tbResolveEmirate();
    } catch (e) {
      /* keep the null default — update() will hide us */
    }

    this.emirate = result.emirate;
    const usable = !!this.emirate && this.emirate !== 'international' && result.inUae;
    this.intlCountry = await this.resolveIntlCountry();

    if (this.intlCountry) {
      // Confirmed GCC destination: fixed 2–4 business-day Aramex promise. Nothing
      // ticks — no cutoff, no date — so no timer is started below.
      this.visible = true;
      this.unknown = false;
    } else if (usable) {
      this.inZone = this.sameDayZone.indexOf(this.emirate) !== -1;
      this.visible = true;
      this.unknown = false;
    } else if (this.geoMode === 'off' || this.unknownFallback) {
      // Area not established. Previously 'off' fell through to the outside-the-zone
      // copy with an empty [emirate], which rendered a dangling "Same-day delivery
      // to ". Both paths now use the neutral, dateless wording instead.
      this.emirate = null;
      this.inZone = false;
      this.visible = true;
      this.unknown = true;
    } else {
      this.visible = false;
      this.unknown = false;
    }

    // Unknown area → back to the placeholder option, never a silently-selected
    // "Dubai" under a "choose your area" line.
    // 'international' (GCC state) isn't an option either: an unmatched value leaves
    // NO option selected, and field-sizing then collapses the select to nothing.
    if (this.selectEl) {
      this.selectEl.value = this.emirate || '';
      if (this.selectEl.selectedIndex === -1) this.selectEl.value = '';
    }

    // GCC state keeps the picker, relabelled "In the UAE?": with the store's country
    // selector switched off it is the ONLY way for someone in Dubai who reads as
    // Saudi (VPN exit, stale country cookie) to get back to the UAE experience —
    // a pick here outranks both (tbResolveCountry).
    if (this.changeLabelEl) {
      const label = this.intlCountry ? this.changeLabelEl.dataset.labelIntl : this.changeLabelEl.dataset.labelUae;
      if (label) this.changeLabelEl.textContent = label;
    }

    this.update();
    if (this.intlCountry) return;
    // Nothing ticks in the unknown state — no countdown, no cutoff to cross —
    // except for bulky items, whose neutral line still flips at the bulky cutoff.
    if (!this.timer && this.visible && (!this.unknown || this.isBulky)) {
      this.timer = setInterval(() => this.update(), 30000);
    }
  }

  /* ISO-2 code of a confirmed GCC (Aramex) destination, or null. */
  resolveIntlCountry() {
    return tbResolveCountry(this.marketCountry, this.intlCountries);
  }

  /* "3 h 40 min" / "40 min" in the page language (tbFormatRemaining is English-only
     and shared with the card bars, so it's left alone). */
  formatRemaining(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const tpl = h > 0 ? this.tpl.timeHM : this.tpl.timeM;
    if (!tpl) return tbFormatRemaining(minutes);
    return tpl.replace('[h]', h).replace('[m]', m);
  }

  /* Arrival date label: "tomorrow, Oct 8" when it's tomorrow, else "Thu, Oct 8". */
  formatEta(etaDate, gst) {
    const tomorrow = new Date(gst.getTime());
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (this.tpl.tomorrow && etaDate.toDateString() === tomorrow.toDateString()) {
      let short;
      try {
        const lang = document.documentElement.lang || 'en';
        short = new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }).format(etaDate);
      } catch (e) {
        short = etaDate.getDate() + '/' + (etaDate.getMonth() + 1);
      }
      return this.tpl.tomorrow.replace('[date]', short);
    }
    return tbFormatDate(etaDate);
  }

  countryName(code) {
    try {
      const lang = document.documentElement.lang || 'en';
      return new Intl.DisplayNames([lang], { type: 'region' }).of(code) || code;
    } catch (e) {
      return code;
    }
  }

  /* Effective order-by cutoff for the resolved emirate: its own courier cutoff,
     capped by the bulky cutoff when the item is heavy. */
  effectiveCutoff(base) {
    if (!this.isBulky) return base;
    if (base == null) return this.bulkyCutoff;
    return Math.min(base, this.bulkyCutoff);
  }

  /* Bulky all-day next-day weekday (Fridays): no cutoff race, no same-day. */
  bulkyAlldayToday(gst) {
    return this.isBulky && this.bulkyAlldayDays.indexOf(gst.getDay()) !== -1;
  }

  /* True once a heavy item can no longer go same-day ANYWHERE — after the bulky
     cutoff or on an all-day weekday. Drives the unknown-area wording too. */
  bulkyNextDayNow(gst) {
    if (!this.isBulky) return false;
    if (this.bulkyAlldayToday(gst)) return true;
    return gst.getHours() * 60 + gst.getMinutes() >= this.bulkyCutoff;
  }

  update() {
    if (!this.visible) {
      this.hidden = true;
      return;
    }

    if (this.carrierEl) this.carrierEl.hidden = !this.intlCountry;
    this.classList.toggle('delivery-eta--intl', !!this.intlCountry);

    if (this.intlCountry) {
      this.hidden = false;
      if (this.titleEl) this.titleEl.textContent = this.tpl.intlTitle;
      if (this.carrierTextEl) {
        this.carrierTextEl.textContent = tbFillTemplate(this.tpl.intlCarrier, { country: this.countryName(this.intlCountry) });
      }
      if (this.subEl) this.subEl.textContent = '';
      this.classList.remove('delivery-eta--today', 'delivery-eta--next', 'delivery-eta--unknown');
      this.sweepKey = null;
      this.renderShipEstimate();
      return;
    }
    if (this.shipEl) this.shipEl.hidden = true;

    if (this.unknown) {
      this.hidden = false;
      // A heavy item past its cutoff must not advertise "same-day in Dubai"
      // even without an area — state the next-day policy instead.
      const nextOnly = this.bulkyNextDayNow(tbGetGSTDate());
      const unknownTitle = nextOnly && this.tpl.unknownTitleNext ? this.tpl.unknownTitleNext : this.tpl.unknownTitle;
      if (this.titleEl) this.titleEl.textContent = unknownTitle;
      // Heavy item past its cutoff: the title already says next-day everywhere,
      // so the "next-day across the rest of the UAE" sub-line would only repeat it.
      if (this.subEl) this.subEl.textContent = nextOnly ? '' : this.tpl.unknownSub;
      this.classList.remove('delivery-eta--today');
      this.classList.remove('delivery-eta--next');
      this.classList.add('delivery-eta--unknown');
      this.sweepKey = null;
      return;
    }
    this.classList.remove('delivery-eta--unknown');

    const cutoff = this.effectiveCutoff(this.inZone ? this.zoneCutoff : this.otherCutoff);
    if (cutoff == null) {
      this.hidden = true;
      return;
    }

    const gst = tbGetGSTDate();
    const nowMinutes = gst.getHours() * 60 + gst.getMinutes();
    const allday = this.bulkyAlldayToday(gst);
    const beforeCutoff = !allday && nowMinutes < cutoff;
    const etaDate = tbDeliveryDate(gst, beforeCutoff, this.skipDays);
    const isToday = etaDate.toDateString() === gst.toDateString();

    // Outside the zone the merchant can suppress the same-day half entirely.
    if (isToday && !this.inZone && !this.showBeforeCutoff) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const vars = {
      emirate: this.labels[this.emirate] || '',
      date: this.formatEta(etaDate, gst),
      cutoff: tbFormatHM(cutoff),
      time: this.formatRemaining(Math.max(0, cutoff - nowMinutes))
    };

    let title;
    if (this.inZone) {
      title = isToday ? this.tpl.zoneToday : this.tpl.zoneNext;
    } else {
      title = isToday ? this.tpl.otherToday : this.tpl.otherNext;
    }
    if (this.titleEl) this.titleEl.textContent = tbFillTemplate(title, vars);
    if (this.subEl) {
      // On a non-delivery weekday "orders after [cutoff]…" would be misleading —
      // the run isn't late, there is no run today. Use the closed-day line instead.
      const skipToday = this.skipDays.indexOf(gst.getDay()) !== -1;
      let subTpl = this.tpl.orderWithin;
      if (!isToday) {
        if (skipToday && this.tpl.closedToday) subTpl = this.tpl.closedToday;
        // Bulky all-day weekday: "orders after 13:00…" would be a false deadline.
        else if (allday && this.tpl.bulkyAllday) subTpl = this.tpl.bulkyAllday;
        else subTpl = this.tpl.afterCutoff;
      }
      this.subEl.textContent = tbFillTemplate(subTpl, vars);
    }

    this.classList.toggle('delivery-eta--today', isToday);
    this.classList.toggle('delivery-eta--next', !isToday);
    this.queueSweep(this.emirate + '|' + etaDate.toDateString());
  }
}

customElements.define('delivery-eta', DeliveryEta);
